import { useState } from "react";
import { mergeClasses } from "@fluentui/react-components";
import {
  DEFAULT_PORTFOLIO_LOG_TAB,
  PORTFOLIO_LOG_TABS,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.constants";
import { useGroupedWorkspaceDashboardStyles } from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.styles";
import {
  IWorkspaceLogTabsProps,
  PortfolioLogTab,
} from "apps/project-iq/components/GroupedWorkspaceDashboard/GroupedWorkspaceDashboard.types";
import { MilestonesLogTable } from "apps/project-iq/components/GroupedWorkspaceDashboard/MilestonesLogTable";
import { DeliverablesLogTable } from "apps/project-iq/components/GroupedWorkspaceDashboard/DeliverablesLogTable";
import { RaidLogTable } from "apps/project-iq/components/GroupedWorkspaceDashboard/RaidLogTable";

const LOG_TAB_COUNT: (
  logTab: PortfolioLogTab,
  logs: IWorkspaceLogTabsProps["logs"],
) => number = (logTab, logs) => {
  switch (logTab) {
    case "milestones":
      return logs.milestones.length;
    case "deliverables":
      return logs.deliverables.length;
    case "raid":
      return logs.raidEntries.length;
  }
};

const getTabId = (logTab: PortfolioLogTab): string =>
  `portfolio-log-tab-${logTab}`;

const getPanelId = (logTab: PortfolioLogTab): string =>
  `portfolio-log-panel-${logTab}`;

export const WorkspaceLogTabs = ({
  logs,
}: IWorkspaceLogTabsProps): JSX.Element => {
  const styles = useGroupedWorkspaceDashboardStyles();
  const [activeLogTab, setActiveLogTab] = useState<PortfolioLogTab>(
    DEFAULT_PORTFOLIO_LOG_TAB,
  );

  return (
    <div>
      <div className={styles.tabStripWrapper}>
        <div
          role="tablist"
          aria-label="Workspace log sections"
          className={styles.tabList}
        >
          {PORTFOLIO_LOG_TABS.map((tabConfig) => {
            const isActive = activeLogTab === tabConfig.tab;
            return (
              <button
                key={tabConfig.tab}
                type="button"
                role="tab"
                id={getTabId(tabConfig.tab)}
                aria-selected={isActive}
                aria-controls={getPanelId(tabConfig.tab)}
                tabIndex={0}
                className={mergeClasses(
                  styles.logTab,
                  isActive && styles.logTabActive,
                )}
                onClick={() => setActiveLogTab(tabConfig.tab)}
              >
                {`${tabConfig.label} · ${LOG_TAB_COUNT(tabConfig.tab, logs)}`}
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.tabPanel}>
        {activeLogTab === "milestones" && (
          <div
            id={getPanelId("milestones")}
            role="tabpanel"
            aria-labelledby={getTabId("milestones")}
            tabIndex={0}
          >
            <MilestonesLogTable milestones={logs.milestones} />
          </div>
        )}
        {activeLogTab === "deliverables" && (
          <div
            id={getPanelId("deliverables")}
            role="tabpanel"
            aria-labelledby={getTabId("deliverables")}
            tabIndex={0}
          >
            <DeliverablesLogTable deliverables={logs.deliverables} />
          </div>
        )}
        {activeLogTab === "raid" && (
          <div
            id={getPanelId("raid")}
            role="tabpanel"
            aria-labelledby={getTabId("raid")}
            tabIndex={0}
          >
            <RaidLogTable raidEntries={logs.raidEntries} />
          </div>
        )}
      </div>
    </div>
  );
};
